import { Box } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { getSmsListRequest, getSmsTypesRequest } from 'redux/reducers/sms/reducer';
import { useDispatch, useSelector } from 'react-redux';
import { getSmsListData, getSmsListLoading } from 'redux/reducers/sms/selectors';
import { useLocation, useParams } from 'react-router-dom';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import queryString from 'query-string';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { TFilters, TOrderBy } from 'redux/types';
import { TSmsListItem } from 'redux/reducers/sms/types';
import { tableStructure } from './tableStructure';
import { SmsTypeHandleFilter } from './SmsTypeHandleFilter';
import { getFiltersFromNavigateParams } from '../../../../../utils/FilterParams';
import { CommunicationHistoryTabs } from '../CommunicationHistoryTabs';
import { TSmsHistoryProps } from './types';

export const SmsHistory = ({ activeTab, onTabChange }: TSmsHistoryProps) => {
	const { emId } = useParams();
	const dispatch = useDispatch();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const smsList = useSelector(getSmsListData);
	const smsListLoading = useSelector(getSmsListLoading);
	const [currentTableStructure, setCurrentTableStructure] = useState(tableStructure);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TSmsListItem>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	const { page } = queryString.parse(search);

	const navigateParamsWithoutPage = getFiltersFromNavigateParams(
		currentTableStructure,
		navigateParams,
	) as TFilters;

	useEffect(() => {
		dispatch(getSmsTypesRequest());
	}, [dispatch]);

	useEffect(() => {
		if (!emId) return;
		const orderBy: TOrderBy[] = [];
		const sortIndex = navigateParams?.sortIndex;
		if (sortIndex !== undefined && sortIndex !== null && currentTableStructure[Number(sortIndex)]) {
			orderBy.push({
				fieldName: currentTableStructure[Number(sortIndex)]?.selector as string,
				direction:
					navigateParams?.sortDirection && String(navigateParams.sortDirection) === 'desc'
						? ESortDirection.DESC
						: ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.DESC });
		navigateParamsWithoutPage.push({ fieldName: 'emId', value: String(emId ?? ''), operator: '=' });
		if (navigateParams?.SearchTypeHandle) {
			navigateParamsWithoutPage.push({
				fieldName: 'typeHandle',
				value: navigateParams.SearchTypeHandle as string,
				operator: '=',
			});
		}

		dispatch(
			getSmsListRequest({
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(navigateParams?.search ?? ''),
				filters: navigateParamsWithoutPage,
				orderBy,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search, emId]);

	return (
		<EasTableWithFilter<TSmsListItem>
			tableStructure={currentTableStructure}
			items={smsList}
			itemsIsLoading={smsListLoading}
			handleAction={() => {}}
			skeletonRows={[1, 1, 1, 1, 1, 1, 1, 1]}
			onTableStructureChange={handleTableStructureChange}
			filterChildrenToEnd={
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						padding: '0 8px',
						gap: 2,
					}}
				>
					<SmsTypeHandleFilter />
				</Box>
			}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					padding: '0 8px',
				}}
			>
				<CommunicationHistoryTabs value={activeTab} onChange={onTabChange} />
			</Box>
		</EasTableWithFilter>
	);
};
